import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Theme = 'cyber' | 'esports' | 'ink' | 'qinghua' | 'terran' | 'zerg' | 'protoss';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const themeNames: Record<Theme, string> = {
  cyber: '赛博朋克',
  esports: '电竞风格',
  ink: '水墨书卷',
  qinghua: '青花瓷韵',
  terran: '人族 Terran',
  zerg: '虫族 Zerg',
  protoss: '神族 Protoss',
};

// 所有可用的主题
const availableThemes: Theme[] = ['cyber', 'esports', 'ink', 'qinghua', 'terran', 'zerg', 'protoss'];

const themeOrder: Theme[] = availableThemes;

export function getThemeName(theme: Theme): string {
  return themeNames[theme];
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('boyangcong-theme');
      return (saved as Theme) || 'cyber';
    }
    return 'cyber';
  });

  useEffect(() => {
    localStorage.setItem('boyangcong-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // 初始化时设置 data-theme 属性
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  const toggleTheme = () => {
    setThemeState(prev => {
      const currentIndex = themeOrder.indexOf(prev);
      const nextIndex = (currentIndex + 1) % themeOrder.length;
      return themeOrder[nextIndex];
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, availableThemes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

